/**
 * `/recruiting/pipeline` — the Pipeline screen, WP-W3. `web/shapes/pipeline.md` is the
 * agreed shape, written before this file.
 *
 * The third recruiting surface, and it copies the Jobs/Candidates pattern (a
 * company-scoped cursor list, a create form) and lays it out as a **board**: one column
 * per pipeline stage, a 44px row per application.
 *
 * ── A column is a list, not a stack of cards ────────────────────────────────
 *
 * A stage is a column of paper: an 11px caps key, the count as the one pill, and a
 * ruled list of names under it. The column header carries **no colour** — a stage is
 * not a severity, and a coloured header would be colour stating no fact about any
 * application in it (DESIGN.md §12, Luật Màu Có Nghĩa). Where an application *is* is
 * the column it is in; where it may go next belongs to the one application the
 * recruiter has opened, so the transition controls — drawn **exactly** from the stage
 * machine — render in the detail below the board rather than on every row of all seven
 * columns.
 *
 * ── Names are resolved, not carried ─────────────────────────────────────────
 *
 * `RecruitingApplication` is two ids and a stage; it holds neither the candidate's name
 * nor the requisition's title. Both are resolved here from the candidate and requisition
 * lists the screen already reads — the candidate list on this screen's own permission
 * (`recruiting.view_candidates`), the requisition list on `recruiting.view_jobs`, which a
 * pipeline-only session may lack. A missing title falls back to the id rather than
 * blocking the board; a missing requisition list turns the filter and the create picker
 * into id inputs (see `CreateApplicationForm`).
 *
 * ── `→ hired` is never issued ────────────────────────────────────────────────
 *
 * The stage machine offers no `hired` button (the `/transition` endpoint refuses it),
 * and the one place a hire is legal — `offer` — shows a **disabled** affordance pointing
 * at the Phase 2 hire action. Nothing here calls `/transition` with `hired`.
 *
 * ── What each refusal does ──────────────────────────────────────────────────
 *
 * Nothing is applied optimistically. A read refused renders `ApiErrorNote` in its
 * region and leaves the others standing. A move refused `409
 * WORKFORCE_INVALID_STATE_TRANSITION` re-reads the board (the stage the card was drawn
 * from is wrong) and prints the server's own `allowed_transitions[]` beside the acting
 * card. A reject refused renders the server's sentence and leaves the reason in place.
 */

import { useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { useSession } from '../../auth/SessionProvider';
import type {
  ApplicationStage,
  RecruitingApplication,
  RecruitingApplicationCreateRequest,
  RecruitingApplicationListResponse,
  RecruitingApplicationRejectRequest,
  RecruitingApplicationTransitionRequest,
  RecruitingCandidateListResponse,
  RecruitingRequisitionListResponse,
  RecruitingStageEventListResponse,
} from '../../api/recruiting';
import {
  RECRUITING_APPLICATIONS_PATH,
  recruitingApplicationRejectPath,
  recruitingApplicationStageEventsPath,
  recruitingApplicationTransitionPath,
  recruitingApplicationsPath,
  recruitingCandidatesPath,
  recruitingRequisitionsPath,
} from '../../api/recruiting';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import { APPLICATION_STAGES, isTerminalStage, STAGE_LABEL } from '../../recruiting/application';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { EmptyState } from '../../states/EmptyState';
import { PartialData } from '../../states/PartialData';
import { Skeleton } from '../../states/Skeleton';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { ApplicationStageControls } from './ApplicationStageControls';
import { ApplicationHistoryPanel } from './ApplicationHistoryPanel';
import { CreateApplicationForm, type PickerOption } from './CreateApplicationForm';

const PAGE_LIMIT = 50;
const DEFAULT_TIMEZONE = 'Asia/Ho_Chi_Minh';

export function PipelineScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? DEFAULT_TIMEZONE;

  const [requisitionFilter, setRequisitionFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // The card whose move is in flight or refused. The transition mutation is shared, so
  // its pending/error is shown against exactly this card and no other.
  const [actingId, setActingId] = useState<string | null>(null);
  const [listToken, setListToken] = useState(0);
  const [historyToken, setHistoryToken] = useState(0);

  const trimmedFilter = requisitionFilter.trim();
  const listPath = recruitingApplicationsPath({
    limit: PAGE_LIMIT,
    requisition_id: trimmedFilter === '' ? undefined : trimmedFilter,
  });
  const list = useApiQuery<RecruitingApplicationListResponse>(listPath, listToken);

  // Enrichment reads: names for the cards, options for the pickers. Independent regions,
  // so a refusal on either leaves the board standing (PRODUCT.md §4).
  const candidates = useApiQuery<RecruitingCandidateListResponse>(
    recruitingCandidatesPath({ limit: PAGE_LIMIT }),
  );
  const requisitions = useApiQuery<RecruitingRequisitionListResponse>(
    recruitingRequisitionsPath({ limit: PAGE_LIMIT }),
  );

  const historyPath = selectedId === null ? null : recruitingApplicationStageEventsPath(selectedId);
  const history = useApiQuery<RecruitingStageEventListResponse>(historyPath, historyToken);

  const createApp = useApiMutation<RecruitingApplicationCreateRequest, RecruitingApplication>(
    RECRUITING_APPLICATIONS_PATH,
    'POST',
  );
  const transition = useApiMutation<RecruitingApplicationTransitionRequest, RecruitingApplication>(
    recruitingApplicationTransitionPath('unset'),
    'POST',
  );
  const reject = useApiMutation<RecruitingApplicationRejectRequest, RecruitingApplication>(
    recruitingApplicationRejectPath('unset'),
    'POST',
  );

  const rows: readonly RecruitingApplication[] = useMemo(() => list.data?.data ?? [], [list.data]);
  const hasMore = list.data?.page.has_more ?? false;

  const candidateName = useMemo(() => {
    const map = new Map<string, string>();
    for (const candidate of candidates.data?.data ?? []) map.set(candidate.id, candidate.full_name);
    return map;
  }, [candidates.data]);

  const requisitionTitle = useMemo(() => {
    const map = new Map<string, string>();
    for (const requisition of requisitions.data?.data ?? []) {
      map.set(requisition.id, requisition.title);
    }
    return map;
  }, [requisitions.data]);

  const candidateOptions: readonly PickerOption[] = useMemo(
    () => (candidates.data?.data ?? []).map((c) => ({ id: c.id, label: c.full_name })),
    [candidates.data],
  );
  const requisitionOptions: readonly PickerOption[] | null = useMemo(() => {
    if (requisitions.status !== 'ok' || requisitions.data === null) return null;
    return requisitions.data.data.map((r) => ({ id: r.id, label: r.title }));
  }, [requisitions.status, requisitions.data]);

  const byStage = useMemo(() => {
    const map = new Map<ApplicationStage, RecruitingApplication[]>();
    for (const stage of APPLICATION_STAGES) map.set(stage, []);
    for (const application of rows) map.get(application.stage)?.push(application);
    return map;
  }, [rows]);

  const settleAfterMove = (): void => {
    setListToken((value) => value + 1);
    setHistoryToken((value) => value + 1);
  };

  const submitCreate = (body: RecruitingApplicationCreateRequest): void => {
    createApp
      .submit(body)
      .then(() => {
        setListToken((value) => value + 1);
      })
      .catch(() => {
        /* `submit` resolves rather than rejects. */
      });
  };

  const moveStage = (application: RecruitingApplication, to: ApplicationStage): void => {
    setActingId(application.id);
    transition
      .submit({ to_stage: to }, recruitingApplicationTransitionPath(application.id))
      .then(settleAfterMove)
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const openCard = (id: string): void => {
    transition.reset();
    reject.reset();
    setActingId(null);
    setSelectedId(id);
  };

  const submitReject = (reason: string): void => {
    if (selectedId === null) return;
    const body: RecruitingApplicationRejectRequest = reason === '' ? {} : { reason };
    reject
      .submit(body, recruitingApplicationRejectPath(selectedId))
      .then(settleAfterMove)
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const selected = selectedId === null ? null : (rows.find((row) => row.id === selectedId) ?? null);

  return (
    <div className="wp-stack wp-f">
      <ScreenHeading>{t('nav.pipeline')}</ScreenHeading>
      <div className="wp-f-lede">
        <p className="wp-small wp-muted">{t('recruiting.pipeline.intro')}</p>
        <p className="wp-small wp-muted" data-testid="w3-scope-note">
          {t('recruiting.pipeline.scopeNote')}
        </p>
      </div>

      <div className="wp-filters">
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w3-req-filter">
            {t('recruiting.pipeline.filter.requisition')}
          </label>
          {requisitionOptions === null ? (
            <>
              <input
                className="wp-field__input"
                id="w3-req-filter"
                data-testid="w3-req-filter"
                type="text"
                value={requisitionFilter}
                autoComplete="off"
                onChange={(event) => {
                  setRequisitionFilter(event.target.value);
                  setSelectedId(null);
                }}
              />
              <p className="wp-small wp-muted">
                {t('recruiting.pipeline.filter.requisitionIdHint')}
              </p>
            </>
          ) : (
            <select
              className="wp-field__input"
              id="w3-req-filter"
              data-testid="w3-req-filter"
              value={requisitionFilter}
              onChange={(event) => {
                setRequisitionFilter(event.target.value);
                setSelectedId(null);
              }}
            >
              <option value="">{t('filters.all')}</option>
              {requisitionOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {list.status === 'loading' ? (
        <Skeleton
          rows={4}
          variant="block"
          label={t('recruiting.pipeline.subjectWord')}
          testId="w3-list-skeleton"
        />
      ) : null}
      {list.status === 'error' && list.error !== null ? (
        <ApiErrorNote
          error={list.error}
          onRetry={list.reload}
          subject={t('recruiting.pipeline.subjectWord')}
          testId="w3-list-error"
        />
      ) : null}

      {list.data === null ? null : rows.length === 0 ? (
        <EmptyState
          title={t('recruiting.pipeline.empty.title')}
          description={t('recruiting.pipeline.empty.body')}
          testId="w3-list-empty"
        />
      ) : (
        <>
          {hasMore ? (
            <PartialData
              excludes={t('recruiting.pipeline.partial.excludes', { shown: rows.length })}
              testId="w3-list-partial"
            />
          ) : null}
          <div className="wp-f-board" data-testid="w3-board">
            {APPLICATION_STAGES.map((stage) => {
              const cards = byStage.get(stage) ?? [];
              return (
                <section
                  className="wp-f-board__col"
                  key={stage}
                  aria-label={t(STAGE_LABEL[stage])}
                  data-testid={`w3-col-${stage}`}
                >
                  {/* The stage is a key, not a severity: 11px caps and the count as
                      the one pill. A coloured column header would have been colour
                      carrying no fact about any application in it. */}
                  <header className="wp-f-board__head">
                    <h3 className="wp-f-board__key">{t(STAGE_LABEL[stage])}</h3>
                    <span className="wp-f-pill" data-testid={`w3-col-count-${stage}`}>
                      <span aria-hidden="true">{cards.length}</span>
                      <span className="wp-visually-hidden">
                        {t('recruiting.pipeline.column.count', { count: cards.length })}
                      </span>
                    </span>
                  </header>
                  {cards.length === 0 ? (
                    <p
                      className="wp-small wp-muted wp-f-board__empty"
                      data-testid={`w3-col-empty-${stage}`}
                    >
                      {t('recruiting.pipeline.column.empty')}
                    </p>
                  ) : (
                    <ul className="wp-f-board__list">
                      {cards.map((application) => (
                        <li
                          className={
                            application.id === selectedId
                              ? 'wp-f-board__row wp-f-board__row--selected'
                              : 'wp-f-board__row'
                          }
                          key={application.id}
                          data-testid={`w3-card-${application.id}`}
                        >
                          {/* The row is the target (Luật 44), and selecting it is what
                              opens the application below the board. */}
                          <button
                            className="wp-f-rowbutton"
                            type="button"
                            data-testid={`w3-open-${application.id}`}
                            aria-pressed={application.id === selectedId}
                            onClick={() => {
                              openCard(application.id);
                            }}
                          >
                            <span
                              className="wp-f-rowbutton__name"
                              data-testid={`w3-card-name-${application.id}`}
                            >
                              {candidateName.get(application.candidate_id) ??
                                application.candidate_id}
                            </span>
                            <span className="wp-small wp-muted">
                              {t('recruiting.pipeline.card.requisition', {
                                requisition:
                                  requisitionTitle.get(application.requisition_id) ??
                                  application.requisition_id,
                              })}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        </>
      )}

      {selectedId === null ? null : (
        <ApplicationHistoryPanel
          status={history.status}
          events={history.data?.data ?? null}
          readError={history.error}
          onReload={history.reload}
          locale={locale}
          timezone={timezone}
          canReject={selected === null ? true : !isTerminalStage(selected.stage)}
          rejectPending={reject.status === 'pending'}
          rejectError={reject.error}
          rejected={reject.status === 'ok'}
          onReject={submitReject}
        >
          {/* The moves live with the opened application, not on every name in every
              column: seven stages of buttons is a wall a recruiter reads past. */}
          {selected === null ? null : (
            <ApplicationStageControls
              appId={selected.id}
              stage={selected.stage}
              pending={transition.status === 'pending' && actingId === selected.id}
              error={actingId === selected.id ? transition.error : null}
              onMove={(to) => {
                moveStage(selected, to);
              }}
            />
          )}
        </ApplicationHistoryPanel>
      )}

      {/* The create form is the last section, not the first: a recruiter opens this
          screen to read the list, and opening a new one is the occasional action.
          Leading with the form put the subject of the page below the fold. */}
      <CreateApplicationForm
        candidateOptions={candidateOptions}
        requisitionOptions={requisitionOptions}
        pending={createApp.status === 'pending'}
        error={createApp.error}
        created={createApp.status === 'ok' ? createApp.data : null}
        onSubmit={submitCreate}
      />
    </div>
  );
}
